import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Sun, Moon, Mail, Languages } from 'lucide-react';
import { useAuth, useLanguage, useTheme } from '../../App';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from '../ui/sheet';
import { LAYOUT_COPY } from '../../seo/publicContent';
import { PUBLIC_PAGES, pagePath, SUPPORT_EMAIL, PUBLISHER } from '../../seo/seoConfig.mjs';

const NAV_KEYS = ['home', 'features', 'guide', 'tips', 'faq'];
const LEGAL_KEYS = ['privacy', 'terms'];

const NavLink = ({ to, active, children, testId, onClick, className = '' }) => (
  <Link
    to={to}
    onClick={onClick}
    data-testid={testId}
    aria-current={active ? 'page' : undefined}
    className={`relative px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--lux-gold)] ${
      active ? 'text-[var(--lux-gold)]' : 'text-white/70 hover:text-white hover:bg-white/5'
    } ${className}`}
  >
    {children}
    {active && <span className="absolute left-3 right-3 -bottom-px h-px bg-[var(--lux-gold)]" aria-hidden="true" />}
  </Link>
);

/**
 * Shell of the public, indexable pages: sticky header (nav + language + theme + CTA),
 * main content and a footer with cross-links. Syncs the app language with the URL.
 */
export const PublicLayout = ({ lang, pageKey, children }) => {
  const { user } = useAuth();
  const { language, setLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const copy = LAYOUT_COPY[lang];
  const otherLang = lang === 'fr' ? 'en' : 'fr';
  const altPath = pagePath(pageKey, otherLang);
  const appHref = user ? (user.onboarding_complete ? '/dashboard' : '/onboarding') : '/auth';

  useEffect(() => {
    if (language !== lang) setLanguage(lang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      // content renders synchronously, but give the layout one frame to settle
      const frame = requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
        else window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      });
      return () => cancelAnimationFrame(frame);
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    return undefined;
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isActive = (key) => key === pageKey || (key === 'tips' && pageKey.startsWith('article_'));

  return (
    <div className="min-h-screen bg-[var(--lux-ink)] text-white lux-noise flex flex-col" data-testid={`public-page-${pageKey}-${lang}`} lang={lang}>
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[60] focus:px-3 focus:py-2 focus:rounded-md focus:bg-[var(--lux-gold)] focus:text-[var(--lux-ink)]">{copy.skipToContent}</a>

      <header className={`sticky top-0 z-40 transition-[background-color,border-color] duration-200 ${scrolled ? 'supports-[backdrop-filter]:bg-black/35 backdrop-blur-md border-b border-white/10' : 'border-b border-transparent'}`} data-testid="public-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          <Link to={pagePath('home', lang)} className="flex items-center gap-2.5 shrink-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--lux-gold)]" data-testid="public-logo-link" aria-label="PrimeFace">
            <img src="/logo.jpeg" alt="" width="36" height="36" className="w-9 h-9 rounded-xl object-cover border border-lux-gold/30" />
            <span className="font-display text-xl text-white">PrimeFace</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1" aria-label="Navigation">
            {NAV_KEYS.map(key => (
              <NavLink key={key} to={pagePath(key, lang)} active={isActive(key)} testId={`public-nav-${key}`}>{copy.nav[key]}</NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to={altPath}
              hrefLang={otherLang}
              lang={otherLang}
              data-testid="public-lang-switch"
              title={copy.switchLang}
              aria-label={copy.switchLang}
              className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-semibold tracking-wide bg-[var(--lux-panel)] border border-white/[0.08] text-white/80 hover:text-[var(--lux-gold)] hover:border-lux-gold/40 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--lux-gold)]"
            >
              <Languages className="w-3.5 h-3.5" /> {copy.switchLangShort}
            </Link>
            <button
              type="button"
              onClick={toggleTheme}
              data-testid="public-theme-toggle"
              aria-label={theme === 'dark' ? copy.themeLight : copy.themeDark}
              title={theme === 'dark' ? copy.themeLight : copy.themeDark}
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[var(--lux-panel)] border border-white/[0.08] text-white/70 hover:text-[var(--lux-gold)] transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--lux-gold)]"
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            {!user && (
              <Button asChild variant="ghost" className="hidden md:inline-flex text-white/80 hover:text-white hover:bg-white/5 h-9" data-testid="public-login-button">
                <Link to="/auth">{copy.login}</Link>
              </Button>
            )}
            <Button asChild className="hidden sm:inline-flex h-9 bg-[var(--lux-gold)] text-[var(--lux-ink)] hover:bg-[var(--lux-gold-2)] active:scale-[0.98] transition-[background-color,transform] duration-200 font-semibold" data-testid="public-header-cta">
              <Link to={appHref}>{user ? copy.dashboard : copy.cta}</Link>
            </Button>

            <Sheet>
              <SheetTrigger asChild>
                <button type="button" className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[var(--lux-panel)] border border-white/[0.08] text-white/80 hover:text-[var(--lux-gold)] transition-colors duration-200" aria-label={copy.menu} data-testid="public-mobile-menu-button">
                  <Menu className="w-5 h-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-[var(--lux-obsidian)] border-l border-white/10 text-white w-[86vw] max-w-sm" data-testid="public-mobile-menu">
                <SheetHeader className="text-left">
                  <SheetTitle className="font-display text-white text-2xl">PrimeFace</SheetTitle>
                </SheetHeader>
                <nav className="mt-6 flex flex-col gap-1" aria-label={copy.menu}>
                  {NAV_KEYS.map(key => (
                    <SheetClose asChild key={key}>
                      <Link to={pagePath(key, lang)} data-testid={`public-mobile-nav-${key}`} aria-current={isActive(key) ? 'page' : undefined} className={`px-3 py-3 rounded-lg text-base font-medium transition-colors duration-200 ${isActive(key) ? 'bg-lux-gold/10 text-[var(--lux-gold)]' : 'text-white/80 hover:bg-white/5 hover:text-white'}`}>
                        {copy.nav[key]}
                      </Link>
                    </SheetClose>
                  ))}
                  <SheetClose asChild>
                    <Link to={altPath} hrefLang={otherLang} lang={otherLang} data-testid="public-mobile-lang-switch" className="px-3 py-3 rounded-lg text-base font-medium text-white/80 hover:bg-white/5 hover:text-white flex items-center gap-2 transition-colors duration-200">
                      <Languages className="w-4 h-4" /> {copy.switchLang}
                    </Link>
                  </SheetClose>
                </nav>
                <div className="mt-8 flex flex-col gap-3">
                  <SheetClose asChild>
                    <Button asChild className="h-11 bg-[var(--lux-gold)] text-[var(--lux-ink)] hover:bg-[var(--lux-gold-2)] font-semibold" data-testid="public-mobile-cta">
                      <Link to={appHref}>{user ? copy.dashboard : copy.cta}</Link>
                    </Button>
                  </SheetClose>
                  {!user && (
                    <SheetClose asChild>
                      <Button asChild variant="outline" className="h-11 bg-[var(--lux-panel)] border-white/10 text-white hover:bg-[var(--lux-panel-2)]" data-testid="public-mobile-login">
                        <Link to="/auth">{copy.login}</Link>
                      </Button>
                    </SheetClose>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main id="main-content" className="flex-1">{children}</main>

      <footer className="border-t border-white/10 mt-24" data-testid="public-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4 space-y-4">
            <div className="flex items-center gap-2.5">
              <img src="/logo.jpeg" alt="" width="36" height="36" className="w-9 h-9 rounded-xl object-cover border border-lux-gold/30" />
              <span className="font-display text-xl text-white">PrimeFace</span>
            </div>
            <p className="text-sm text-white/50 leading-relaxed max-w-sm">{copy.footerTagline}</p>
            <p className="text-xs text-white/30 leading-relaxed max-w-md">{copy.disclaimer}</p>
          </div>
          <div className="md:col-span-2">
            <p className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-4">{copy.footerPages}</p>
            <ul className="space-y-2.5">
              {NAV_KEYS.map(key => (
                <li key={key}><Link to={pagePath(key, lang)} className="text-sm text-white/70 hover:text-[var(--lux-gold)] transition-colors duration-200" data-testid={`public-footer-${key}`}>{copy.nav[key]}</Link></li>
              ))}
              <li><Link to={appHref} className="text-sm text-white/70 hover:text-[var(--lux-gold)] transition-colors duration-200" data-testid="public-footer-app">{user ? copy.dashboard : copy.login}</Link></li>
            </ul>
          </div>
          <div className="md:col-span-2">
            <p className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-4">{copy.footerLegal}</p>
            <ul className="space-y-2.5">
              {LEGAL_KEYS.map(key => (
                <li key={key}><Link to={pagePath(key, lang)} className="text-sm text-white/70 hover:text-[var(--lux-gold)] transition-colors duration-200" data-testid={`public-footer-${key}`}>{copy.nav[key]}</Link></li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-2">
            <p className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-4">{copy.footerLanguages}</p>
            <ul className="space-y-2.5">
              {Object.keys(PUBLIC_PAGES.home.paths).map(l => (
                <li key={l}><Link to={pagePath(pageKey, l)} hrefLang={l} lang={l} aria-current={l === lang ? 'true' : undefined} className={`text-sm transition-colors duration-200 ${l === lang ? 'text-[var(--lux-gold)]' : 'text-white/70 hover:text-[var(--lux-gold)]'}`} data-testid={`public-footer-lang-${l}`}>{l === 'fr' ? 'Fran\u00e7ais' : 'English'}</Link></li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-2">
            <p className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-4">{copy.footerContact}</p>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[var(--lux-gold)] transition-colors duration-200 break-all" data-testid="public-footer-email">
              <Mail className="w-4 h-4 shrink-0" /> {SUPPORT_EMAIL}
            </a>
          </div>
        </div>
        <div className="border-t border-white/[0.06]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 text-xs text-white/35 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <span>PrimeFace {'\u00a9'} {new Date().getFullYear()} {PUBLISHER}. {copy.rights}</span>
            <span>primeface.io</span>
          </div>
        </div>
      </footer>
    </div>
  );
};
